import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash, Check, X, Tag as TagIcon } from "lucide-react";
import {
  ExpenseCategoryDTO,
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
} from "../../api/expenses";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { Button, Card, ConfirmDialog, EmptyState, IconButton, Input, Loader } from "../../ui";
import "./styles.scss";

type Props = {
  canManage: boolean;
};

const ExpenseCategoriesManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const [categories, setCategories] = useState<ExpenseCategoryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getExpenseCategories();
      const items = res?.data?.data ?? res?.data ?? [];
      setCategories(Array.isArray(items) ? items : []);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to load categories" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createExpenseCategory({ name: newName.trim() });
      dispatch(setGeneralAlertMessage({ type: "success", message: "Category added" }));
      setNewName("");
      await fetchCategories();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to add category" }));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (cat: ExpenseCategoryDTO) => {
    setEditingId(cat.id);
    setEditingName(cat.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (id: string) => {
    if (!editingName.trim()) return;
    setBusyId(id);
    try {
      await updateExpenseCategory(id, { name: editingName.trim() });
      dispatch(setGeneralAlertMessage({ type: "success", message: "Category updated" }));
      setEditingId(null);
      setEditingName("");
      await fetchCategories();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to update category" }));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusyId(id);
    try {
      await deleteExpenseCategory(id);
      dispatch(setGeneralAlertMessage({ type: "success", message: "Category deleted" }));
      setConfirmDeleteId(null);
      await fetchCategories();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to delete category" }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card
      className="expense-categories-management"
      title="Expense Categories"
      subtitle={loading ? "Loading…" : `${categories.length} categor${categories.length === 1 ? "y" : "ies"}`}
      padding="none"
    >
      {canManage && (
        <div className="ec-add-row">
          <Input
            type="text"
            aria-label="New category name"
            placeholder="New category name (e.g. Equipment)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) handleCreate();
            }}
            disabled={creating}
          />
          <Button icon={<Plus size={16} />} onClick={handleCreate} loading={creating} disabled={!newName.trim()}>
            {creating ? "Adding…" : "Add"}
          </Button>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : categories.length === 0 ? (
        <EmptyState compact icon={<TagIcon />} title="No expense categories yet." />
      ) : (
        <ul className="ec-list">
          {categories.map((cat) => {
            const isEditing = editingId === cat.id;
            const isBusy = busyId === cat.id;
            return (
              <li key={cat.id} className="ec-item">
                {isEditing ? (
                  <Input
                    type="text"
                    inputSize="sm"
                    aria-label="Category name"
                    className="ec-edit-input"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(cat.id);
                      if (e.key === "Escape") cancelEdit();
                    }}
                    autoFocus
                    disabled={isBusy}
                  />
                ) : (
                  <div className="ec-info">
                    <span className="ec-name">{cat.name}</span>
                    <span className="ec-count">
                      {cat.expenseCount} expense{cat.expenseCount === 1 ? "" : "s"}
                    </span>
                  </div>
                )}

                {canManage && (
                  <div className="ec-actions">
                    {isEditing ? (
                      <>
                        <IconButton
                          label="Save"
                          variant="tint"
                          size="sm"
                          icon={<Check size={14} />}
                          onClick={() => saveEdit(cat.id)}
                          disabled={!editingName.trim() || isBusy}
                        />
                        <IconButton
                          label="Cancel"
                          size="sm"
                          icon={<X size={14} />}
                          onClick={cancelEdit}
                          disabled={isBusy}
                        />
                      </>
                    ) : (
                      <>
                        <IconButton
                          label="Rename"
                          size="sm"
                          icon={<Pencil size={14} />}
                          onClick={() => startEdit(cat)}
                          disabled={isBusy}
                        />
                        <IconButton
                          label="Delete"
                          variant="danger"
                          size="sm"
                          icon={<Trash size={14} />}
                          onClick={() => setConfirmDeleteId(cat.id)}
                          disabled={isBusy}
                        />
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmDeleteId}
        // Cancel / ✕ / Escape close it; after Delete it closes only when the delete worked (handleDelete clears it)
        onClose={(confirmed) => {
          if (!confirmed) setConfirmDeleteId(null);
        }}
        onConfirm={() => (confirmDeleteId ? handleDelete(confirmDeleteId) : undefined)}
        loading={!!confirmDeleteId && busyId === confirmDeleteId}
        title="Delete this category?"
        message={
          <>
            This cannot be undone. Categories that are still in use by an expense entry
            cannot be deleted — reassign or delete those expenses first.
          </>
        }
        confirmLabel={confirmDeleteId && busyId === confirmDeleteId ? "Deleting…" : "Delete"}
      />
    </Card>
  );
};

export default ExpenseCategoriesManagement;
